import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { getMockData, META } from "../lib/mockData";
import { formatPct } from "../lib/csv";

const DATE_RANGES = [
  { value: "60", label: "Last 60 days" },
  { value: "30", label: "Last 30 days" },
  { value: "14", label: "Last 14 days" },
  { value: "7", label: "Last 7 days" },
];

const THRESHOLDS = ["5", "10", "15"];

export default function Workspace() {
  const data = useMemo(() => getMockData(), []);
  const [department, setDepartment] = useState("all");
  const [floor, setFloor] = useState("all");
  const [range, setRange] = useState("60");
  const [threshold, setThreshold] = useState("10");

  const adjust = (rate) => {
    // Threshold simulates stricter detection: higher threshold => slightly lower occupancy.
    const factor = threshold === "5" ? 1.04 : threshold === "10" ? 1.0 : 0.94;
    const dateFactor = range === "7" ? 1.03 : range === "14" ? 1.02 : range === "30" ? 1.01 : 1.0;
    return Math.max(0.05, Math.min(0.98, rate * factor * dateFactor));
  };

  const departmentRows = data.departmentStats
    .filter((d) => department === "all" || d.department === department)
    .map((d) => ({ ...d, occupancyRate: adjust(d.occupancyRate) }));

  const floorRows = data.floorStats
    .filter((f) => floor === "all" || String(f.floor) === floor)
    .map((f) => ({ ...f, occupancyRate: adjust(f.occupancyRate) }));

  const usedVsUnused = data.departmentStats.map((d) => {
    const adj = adjust(d.occupancyRate);
    const used = Math.round(d.allocatedDesks * adj);
    return {
      name: d.department,
      Used: used,
      Unused: d.allocatedDesks - used,
    };
  });

  const totalUsed = floorRows.reduce((s, f) => s + Math.round(f.totalWorkplaces * f.occupancyRate), 0);
  const totalSeats = floorRows.reduce((s, f) => s + f.totalWorkplaces, 0);

  return (
    <div data-testid="page-workspace">
      <PageHeader
        title="Workspace utilization"
        subtitle="Drill into desk usage by department, floor and detection threshold."
        testId="workspace-header"
      />

      <Card className="mb-6 border-slate-200 shadow-sm" data-testid="filter-bar">
        <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs font-medium text-slate-500" htmlFor="ws-dept">
              Department
            </label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger id="ws-dept" data-testid="filter-department" className="mt-1">
                <SelectValue placeholder="All departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {META.DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500" htmlFor="ws-floor">
              Floor
            </label>
            <Select value={floor} onValueChange={setFloor}>
              <SelectTrigger id="ws-floor" data-testid="filter-floor" className="mt-1">
                <SelectValue placeholder="All floors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All floors</SelectItem>
                {META.FLOORS.map((f) => (
                  <SelectItem key={f} value={String(f)}>
                    Floor {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500" htmlFor="ws-range">
              Date range
            </label>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger id="ws-range" data-testid="filter-range" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500" htmlFor="ws-threshold">
              Occupancy threshold
            </label>
            <Select value={threshold} onValueChange={setThreshold}>
              <SelectTrigger id="ws-threshold" data-testid="filter-threshold" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {THRESHOLDS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t} min minimum presence
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary band */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="border-slate-200 shadow-sm" data-testid="summary-total">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Workplaces in scope
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {totalSeats}
            </div>
            <div className="text-xs text-slate-500">Filtered selection</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="summary-used">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Avg used
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {totalUsed}
            </div>
            <div className="text-xs text-emerald-700">
              {formatPct(totalUsed / Math.max(1, totalSeats))} occupancy
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm" data-testid="summary-unused">
          <CardContent className="p-5">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Avg unused
            </div>
            <div className="mt-2 font-heading text-3xl font-semibold text-slate-900">
              {totalSeats - totalUsed}
            </div>
            <div className="text-xs text-rose-700">
              {formatPct((totalSeats - totalUsed) / Math.max(1, totalSeats))} idle
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Used vs Unused chart */}
      <Card className="mb-6 border-slate-200 shadow-sm" data-testid="card-used-vs-unused">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg font-medium text-slate-900">
            Used vs unused workplaces by department
          </CardTitle>
          <p className="text-xs text-slate-500">
            Stacked bars · {threshold}-min detection threshold
          </p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usedVsUnused} margin={{ top: 8, right: 12, bottom: 16, left: -8 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  angle={-20}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Used" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Unused" stackId="a" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Tables */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 shadow-sm" data-testid="table-department">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Occupancy rate per department
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Allocated</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Occupancy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departmentRows.map((d) => (
                  <TableRow key={d.department} data-testid={`row-dept-${d.department}`}>
                    <TableCell className="font-medium text-slate-900">
                      {d.department}
                    </TableCell>
                    <TableCell className="text-right">{d.allocatedDesks}</TableCell>
                    <TableCell className="text-right">{Math.round(d.allocatedDesks * d.occupancyRate)}</TableCell>
                    <TableCell className="text-right">
                      <Badge
                        className={
                          d.occupancyRate < 0.4
                            ? "bg-rose-50 text-rose-700"
                            : d.occupancyRate < 0.6
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                        }
                        variant="secondary"
                      >
                        {formatPct(d.occupancyRate)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="table-floor">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg font-medium text-slate-900">
              Occupancy rate per floor
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Floor</TableHead>
                  <TableHead className="text-right">Workplaces</TableHead>
                  <TableHead className="text-right">Avg used</TableHead>
                  <TableHead className="text-right">Occupancy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {floorRows.map((f) => (
                  <TableRow key={f.floor} data-testid={`row-floor-${f.floor}`}>
                    <TableCell className="font-medium text-slate-900">
                      Floor {f.floor}
                    </TableCell>
                    <TableCell className="text-right">{f.totalWorkplaces}</TableCell>
                    <TableCell className="text-right">
                      {Math.round(f.totalWorkplaces * f.occupancyRate)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        className={
                          f.occupancyRate < 0.4
                            ? "bg-rose-50 text-rose-700"
                            : f.occupancyRate < 0.6
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                        }
                        variant="secondary"
                      >
                        {formatPct(f.occupancyRate)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
